import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { settingService } from '../../services';
import { useToast } from '../../hooks/useToast';

const ParameterSettingsView = ({ searchTrigger = 0 }) => {
  const toast = useToast();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingService.getSettings();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('无法获取系统配置');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [searchTrigger]);

  const handleInputChange = (key, value) => {
    setSettings(prev => {
      let newSettings = prev.map(s => 
        s.key === key ? { ...s, value } : s
      );

      // 如果更新的是达标类型，自动同步达标赠送课时数
      if (key === 'finance_default_bonus_type') {
        const match = value.match(/送(\d+(\.\d+)?)节/);
        if (match) {
          const hours = match[1];
          newSettings = newSettings.map(s => 
            s.key === 'finance_default_bonus_hours' ? { ...s, value: hours } : s
          );
        }
      }
      
      return newSettings;
    });
  };

  const handleSave = async () => {
    try {
      if (!Array.isArray(settings)) return;
      
      setSaving(true);
      await settingService.updateSettings(settings.map(s => ({
        key: s.key,
        value: s.value
      })));
      toast.success('设置已保存');
      loadSettings(); // 重新加载以获取更新时间等信息
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('保存设置失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <RefreshCw size={32} className="text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-gray-500">正在获取配置参数...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pb-20 mt-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">加载失败</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <button onClick={loadSettings} className="mt-2 text-xs text-red-700 underline">
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 安全检查，确保 settings 是数组
  const safeSettings = Array.isArray(settings) ? settings : [];

    // 按分组对设置进行归类
  const financeSettings = safeSettings.filter(s => s.group === 'finance');
  const rewardSettings = safeSettings.filter(s => s.group === 'reward');

  return (
    <div className="space-y-4 pb-20 pt-2 text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 账务默认参数 */}
        <Card className="p-3">
          <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-2">
            <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center">
              <Settings size={14} className="text-blue-600" />
            </div>
            <h2 className="text-base font-bold text-gray-800">账务默认参数</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {financeSettings.map(setting => (
              <div key={setting.key} className="flex flex-col">
                <label className="block text-[13px] font-bold text-gray-700 mb-0.5 truncate" title={setting.label}>
                  {setting.label}
                </label>
                <div className="relative">
                  <input
                    type={setting.key.includes('hours') || setting.key.includes('price') ? 'number' : 'text'}
                    value={setting.value}
                    onChange={(e) => handleInputChange(setting.key, e.target.value)}
                    className="w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm"
                  />
                  {setting.key.includes('hours') && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]">节</span>
                  )}
                  {setting.key.includes('price') && (
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]">¥</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-tight h-8" title={setting.description}>
                  {setting.description}
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* 赠送规则参数 */}
        <Card className="p-3">
          <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-2">
            <div className="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center">
              <Settings size={14} className="text-purple-600" />
            </div>
            <h2 className="text-base font-bold text-gray-800">赠送规则参数</h2>
          </div>
          
          <div className="space-y-4">
            {/* 推荐赠送 */}
            <div className="space-y-2">
              <h3 className="text-[13px] font-bold text-purple-600 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                推荐赠送
              </h3>
              <div className="grid grid-cols-2 gap-x-4">
                {rewardSettings.filter(s => s.key.includes('referral')).map(setting => (
                  <div key={setting.key} className="flex flex-col">
                    <label className="block text-[13px] font-bold text-gray-700 mb-0.5 truncate" title={setting.label}>
                      {setting.label}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={setting.value}
                        onChange={(e) => handleInputChange(setting.key, e.target.value)}
                        className="w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]">节</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-tight h-8" title={setting.description}>
                      {setting.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 达标赠送 */}
            <div className="space-y-2 pt-3 border-t border-gray-100">
              <h3 className="text-[13px] font-bold text-blue-600 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                达标赠送
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {rewardSettings.filter(s => s.key.includes('bonus')).map(setting => (
                  <div key={setting.key} className="flex flex-col">
                    <label className="block text-[13px] font-bold text-gray-700 mb-0.5 truncate" title={setting.label}>
                      {setting.label}
                    </label>
                    <div className="relative">
                      <input
                        type={setting.key.includes('hours') ? 'number' : 'text'}
                        value={setting.value}
                        onChange={(e) => handleInputChange(setting.key, e.target.value)}
                        className={`w-full px-2 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm ${
                          setting.key === 'finance_default_bonus_hours' 
                            ? 'bg-blue-50 text-blue-700 font-bold cursor-not-allowed' 
                            : 'bg-gray-50 text-gray-800'
                        }`}
                        readOnly={setting.key === 'finance_default_bonus_hours'}
                      />
                      {setting.key.includes('hours') && (
                        <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[11px] ${
                          setting.key === 'finance_default_bonus_hours' ? 'text-blue-400' : 'text-gray-400'
                        }`}>节</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-tight h-8" title={setting.description}>
                      {setting.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-5 p-3 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
            <CheckCircle2 size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 leading-relaxed">
              <p className="font-bold mb-0.5">配置说明：</p>
              <ul className="list-disc pl-4 space-y-0.5 opacity-80">
                <li>账务参数将应用于“账务新增”时的自动填充。</li>
                <li>推荐人结算达标数修改后，即刻对所有“在途”记录生效。</li>
                <li>所有数值修改需点击底部“保存修改”方能生效。</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <div className="sticky bottom-0 left-0 right-0 -mx-4 px-4 py-1 z-40 bg-gradient-to-t from-[#F7FAFC] via-[#F7FAFC]/95 to-transparent">
        <Button
          variant="primary"
          icon={Save}
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3.5 shadow-lg shadow-blue-200/40 rounded-xl flex justify-center items-center gap-2"
        >
          {saving ? '正在保存...' : '保存修改'}
        </Button>
      </div>
    </div>
  );
};

export default ParameterSettingsView;
